import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { format } from 'date-fns'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Typography,
    Stack,
    Chip,
    Box,
    IconButton,
    Tooltip,
    Divider,
    Alert,
    CircularProgress
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import RefreshIcon from '@mui/icons-material/Refresh'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'

import { DesmosActStatus } from '@/core/models/consultation.model'
import { useRetryDesmosActsMutation, useGetConsultationQuery, useAddManualDesmosActMutation } from '@/core/services/consultation'
import { useSnackbar } from 'notistack'
import SimpleDesmosActSelectionDialog, { SimpleActDto } from './SimpleDesmosActSelectionDialog'

interface DesmosActsDetailDialogProps {
    open: boolean
    onClose: () => void
    consultationId: string
    desmosActStatus?: DesmosActStatus
    networkId: string
    waitingRoomId: string
}

export default function DesmosActsDetailDialog({
    open,
    onClose,
    consultationId,
    desmosActStatus: initialDesmosActStatus,
    networkId,
    waitingRoomId
}: DesmosActsDetailDialogProps) {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const [retryDesmosActs, { isLoading: isRetrying }] = useRetryDesmosActsMutation()
    const [addManualDesmosAct, { isLoading: isCreatingAct }] = useAddManualDesmosActMutation()
    const { data: consultation } = useGetConsultationQuery(consultationId, { skip: !open })
    const [showAddManualDialog, setShowAddManualDialog] = useState(false)

    // Use fresh data from consultation query if available, fallback to prop
    const desmosActStatus = consultation?.desmosActStatus || initialDesmosActStatus

    if (!desmosActStatus) {
        return null
    }

    const { success, message, createdAt, acts = [] } = desmosActStatus
    const successfulActs = acts.filter(act => act.success)
    const failedActs = acts.filter(act => !act.success)

    const handleRetry = async () => {
        try {
            await retryDesmosActs({
                networkId,
                waitingRoomId,
                consultationId
            }).unwrap()

            enqueueSnackbar(t('notification.desmos.retry.success'), { variant: 'success' })
            onClose()
        } catch (error) {
            console.error('Retry error:', error)
            enqueueSnackbar(t('notification.desmos.retry.error'), { variant: 'error' })
        }
    }

    const handleAddManualAct = () => {
        setShowAddManualDialog(true)
    }

    const handleCreateManualAct = async (actValues: SimpleActDto) => {
        if (!consultation?.patient) {
            enqueueSnackbar(t('error.patient.notFound', { defaultValue: 'Patient not found' }), { variant: 'error' })
            return
        }

        try {
            // Create the act and update consultation in one call
            // Practitioner info (nom, prenom, rpps) will be taken from consultation by backend
            await addManualDesmosAct({
                consultationId,
                actData: {
                    libelle: actValues.libelle,
                    date_realisation: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
                    honoraire: actValues.honoraire,
                    id_transposition: actValues.id_transposition,
                    supplement: actValues.supplement || '0',
                    modalite_complementaire: {
                        code_situation_parcours_de_soin: null,
                        nom_medecin_parcours_de_soins: null,
                        prenom_medecin_parcours_de_soins: null,
                        code_majoration_ou_modificateurs: null,
                        code_justif_exoneration_acte: null,
                        code_qualificatif_depence: null,
                        code_association: null,
                        is_remboursement_exceptionnel: null,
                        is_ald: null,
                        taux_abattement: null,
                    }
                },
            }).unwrap()

            enqueueSnackbar(t('notification.desmos.act.create.success'), { variant: 'success' })
            setShowAddManualDialog(false)

            // Keep main dialog open so user can see the updated status with the new act
            // The consultation query will auto-refresh via cache invalidation
        } catch (error) {
            console.error('Error creating Desmos act:', error)
            enqueueSnackbar(t('notification.desmos.act.create.error'), { variant: 'error' })
        }
    }

    const handleCloseManualDialog = () => {
        setShowAddManualDialog(false)
    }

    const getStatusIcon = (actSuccess: boolean) => {
        return actSuccess ? (
            <CheckCircleIcon color="success" />
        ) : (
            <ErrorIcon color="error" />
        )
    }

    const getStatusColor = () => {
        if (!success) return 'error'
        if (failedActs.length > 0) return 'warning'
        return 'success'
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{t('page.network.desmos.title')}</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={3}>
                    {/* Info Banner */}
                    <Alert severity="info" icon={<InfoIcon />}>
                        <Typography variant="body2">
                            {t('page.network.desmos.infoBanner')}
                        </Typography>
                    </Alert>

                    {/* Overall Status */}
                    <Box>
                        <Alert severity={getStatusColor()} icon={<InfoIcon />}>
                            <Typography variant="body2">
                                <strong>{t('page.network.desmos.autoCreationStatus')}:</strong> {message}
                            </Typography>
                            {createdAt && (
                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                    {t('page.network.desmos.processedAt')}: {dayjs(createdAt).format('DD/MM/YYYY HH:mm:ss')}
                                </Typography>
                            )}
                        </Alert>
                    </Box>

                    {/* Summary */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('page.network.desmos.summary.title')}:
                        </Typography>
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                            <Chip
                                label={`${t('page.network.desmos.summary.totalAttempted')}: ${acts.length}`}
                                color="default"
                                variant="outlined"
                            />
                            <Chip
                                label={`${t('page.network.desmos.summary.successful')}: ${successfulActs.length}`}
                                color="success"
                                variant="outlined"
                            />
                            {failedActs.length > 0 && (
                                <Chip
                                    label={`${t('page.network.desmos.summary.failed')}: ${failedActs.length}`}
                                    color="error"
                                    variant="outlined"
                                />
                            )}
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Acts List */}
                    {acts.length > 0 ? (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {t('page.network.desmos.details.title')}:
                            </Typography>
                            <List>
                                {acts.map((act, index) => (
                                    <ListItem
                                        key={index}
                                        sx={{
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            mb: 1,
                                            backgroundColor: theme => act.success
                                                ? theme.palette.mode === 'light' ? 'rgba(46, 125, 50, 0.08)' : 'rgba(102, 187, 106, 0.16)'
                                                : theme.palette.mode === 'light' ? 'rgba(211, 47, 47, 0.08)' : 'rgba(244, 67, 54, 0.16)'
                                        }}
                                    >
                                        <ListItemIcon>{getStatusIcon(act.success)}</ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography variant="subtitle2">{act.libelle}</Typography>
                                                    {act.success && act.actId && (
                                                        <Chip
                                                            label={`ID: ${act.actId}`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Stack>
                                            }
                                            secondary={
                                                <>
                                                    {act.success ? (
                                                        <Typography variant="caption" color="success.dark">
                                                            {t('page.network.desmos.details.successMessage')}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="caption" color="error.dark">
                                                            {t('page.network.desmos.details.error')}: {act.error || t('page.network.desmos.details.unknownError')}
                                                        </Typography>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    ) : (
                        <Alert severity="info">
                            <Typography variant="body2">
                                {t('page.network.desmos.details.noActsConfigured')}
                            </Typography>
                        </Alert>
                    )}

                    <Divider />

                    {/* Action Buttons - Always visible for accounting control */}
                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            {t('page.network.desmos.actions.title')}:
                        </Typography>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="caption">
                                <strong>{t('page.network.desmos.guidance.title')}:</strong><br />
                                • {t('page.network.desmos.guidance.additionalProcedures')}<br />
                                • {t('page.network.desmos.guidance.specialistConsultation')}<br />
                                • {t('page.network.desmos.guidance.additionalImaging')}<br />
                                • {t('page.network.desmos.guidance.emergencyInterventions')}<br />
                                • {t('page.network.desmos.guidance.anyBillableService')}
                            </Typography>
                        </Alert>
                        <Stack direction="row" spacing={2} flexWrap="wrap">
                            {failedActs.length > 0 && (
                                <Tooltip title={t('page.network.desmos.actions.retryTooltip')}>
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        startIcon={isRetrying ? <CircularProgress size={16} /> : <RefreshIcon />}
                                        onClick={handleRetry}
                                        disabled={isRetrying}
                                    >
                                        {t('page.network.desmos.actions.retryFailed')} ({failedActs.length})
                                    </Button>
                                </Tooltip>
                            )}
                            <Tooltip title={t('page.network.desmos.actions.addTooltip')}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddCircleIcon />}
                                    onClick={handleAddManualAct}
                                >
                                    {t('page.network.desmos.actions.addAdditional')}
                                </Button>
                            </Tooltip>
                        </Stack>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    {t('common.close')}
                </Button>
            </DialogActions>

            {/* Manual Act Creation Dialog */}
            <SimpleDesmosActSelectionDialog
                open={showAddManualDialog}
                onClose={handleCloseManualDialog}
                onSelectAct={handleCreateManualAct}
                title={t('page.network.desmos.actSelection.title')}
                description={t('page.network.desmos.actSelection.description')}
            />
        </Dialog>
    )
}

